import { usePdfAsImage } from 'features/knowledgeDocumentViewer/components/viewer/hooks/usePdfAsImage'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/types'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  FilterOption,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { DocumentViewerPage } from 'features/knowledgeDocumentViewer/types'
import { useMemo } from 'react'
import { BoundingBoxJsonItem } from 'types'

import { useImageNaturalSize } from './useImageNaturalSize'

export function useImageViewerLogic(
  pdfUrl: string | undefined,
  pageNumber: number,
  scale: number,
  pageIndex: DocumentViewerPage,
  totalPages: number
) {
  const { filterOptions, assetTypeFilters, currentDocumentQuery } =
    useKnowledgeDocumentUXContext()
  const { boundingBoxDataState } = useKnowledgeDocumentDataContext()

  // Convert PDF to image
  const { imageUrl, isLoading } = usePdfAsImage(
    pdfUrl,
    pageNumber,
    scale,
    totalPages
  )

  // Get actual image dimensions after loading (not canvas dimensions)
  const dims = useImageNaturalSize(imageUrl)

  // Calculate boxes in pixel coordinates using the loaded image's natural dimensions
  // Don't show boxes while PDF is loading to prevent showing old boxes on new image
  const {
    confirmedList,
    unconfirmedList,
  }: { confirmedList: Box[]; unconfirmedList: Box[] } = useMemo(() => {
    if (!dims || isLoading) return { confirmedList: [], unconfirmedList: [] }

    const mapListFunction = (
      list: BoundingBoxJsonItem[],
      confirmed: boolean
    ) => {
      return list.map((it) => {
        return {
          id: `${String(it.id)}-${pageIndex}`,
          confirmed: confirmed,
          x: it.bounding_box.left * dims.w,
          y: it.bounding_box.top * dims.h,
          w: it.bounding_box.width * dims.w,
          h: it.bounding_box.height * dims.h,
          label: it.text ?? String(it.id),
          kind: 'text' as const,
          groupId: it.groupId,
          type: it.type,
          linkedUnitId: it.linkedUnitId,
          assetId: it.assetId,
        }
      })
    }

    const confirmedList = mapListFunction(
      boundingBoxDataState?.[pageIndex]?.confirmed ?? [],
      true
    )
    const unconfirmedList = mapListFunction(
      boundingBoxDataState?.[pageIndex]?.unconfirmed ?? [],
      false
    )
    return { confirmedList, unconfirmedList }
  }, [boundingBoxDataState, dims, isLoading, pageIndex])

  const filteredBoxes = useMemo(() => {
    let filteredList: Box[] = []
    if (filterOptions === FilterOption.ShowUnconfirmed) {
      filteredList = unconfirmedList
    } else if (filterOptions === FilterOption.ShowConfirmed) {
      filteredList = confirmedList
    } else if (filterOptions === FilterOption.RemoveBoxes) {
      return []
    } else {
      filteredList = [...confirmedList, ...unconfirmedList]
    }

    // Apply asset type filter
    if (assetTypeFilters.length > 0) {
      filteredList = filteredList.filter(
        (box) => box.type && assetTypeFilters.includes(box.type)
      )
    }
    if (currentDocumentQuery.trim() === '') {
      return filteredList
    }

    const searchLower = currentDocumentQuery.toLowerCase()
    return filteredList.filter((box) =>
      box.label?.toLowerCase().includes(searchLower)
    )
  }, [
    assetTypeFilters,
    confirmedList,
    currentDocumentQuery,
    filterOptions,
    unconfirmedList,
  ])

  return {
    imageUrl,
    dims,
    boxes: filteredBoxes,
    isLoading,
  }
}
